(ns ductile.ui.mapbox
  (:require [clojure.string :as str]
            [ductile.ui.locale :as locale]
            [nextjournal.clerk.render.hooks :as hooks]))

(def map-opts-defaults {:style "mapbox://styles/ductile/clxbm7c55029q01pn72ehh68d"
                        :center [11.624590, 48.603691]
                        :zoom 9})

(def directions-defaults {:accessToken (when (exists? js/mapboxgl)
                                         (.-accessToken js/mapboxgl))
                          :interactive false
                          :flyTo {:maxDuration 0 :screenSpeed 0}
                          :controls {:inputs false
                                     :instructions false
                                     :profileSwitcher false}})

(defn- event-keys
  "Get all keys in map that start with `:on-`"
  [m]
  (filter #(str/starts-with? (name %) "on-") (keys m)))

(defn- handle-events
  "Given an object, a map, and a sequence of keys (from [[event-keys]]), attach
  event handlers to said object. The handlers will receive the object as their
  first argument.

  {:on-foo handler} -> (.on obj \"foo\" (partial handler obj))
  "
  [^js this m ks]
  (doseq [k ks]
    (.on this (subs (name k) 3) (partial (get m k) this))))

(defn ensure-locale! [^js the-map]
  (.once the-map "style.load"
         (fn [] (let [language-code (name @locale/!current-locale)
                      ^js ml (js/MapboxLanguage. #js{:defaultLanguage language-code})]
                  (.addControl the-map ml)
                  (.setStyle the-map (.setLanguage ml (.getStyle the-map) language-code) #js{:diff false})))))

(defn map-ref
  "Returns a ref function to attach a map to a given dom element.

  Event handlers can be attached to the map by passing keys starting with
  `:on-`, like `:on-load`, `:on-render`, etc. They will receive the mapbox map
  object as their first argument.

  Call from a [[reagent/with-let]], not inline, as demonstrated in the
  defscene."
  [opts]
  (fn [el]
    (when (and (exists? js/mapboxgl) el)
      (let [map-opts    (assoc map-opts-defaults :container el)
            event-keys  (event-keys opts)
            map-opts    (into map-opts (apply dissoc opts event-keys))
            the-map (js/mapboxgl.Map. (clj->js map-opts))]
        (ensure-locale! the-map)
        (handle-events the-map opts event-keys)))))

(defn use-map
  ([!container-ref opts]
   (use-map !container-ref opts []))
  ([!container-ref opts deps]
   (hooks/use-effect
    (fn []
      #_(js/console.log "add map")
      (when @!container-ref
        (let [container @!container-ref
              map-opts (assoc map-opts-defaults :container container)
              event-keys (event-keys opts)
              map-opts (into map-opts (apply dissoc opts event-keys))]
          (try
            (let [the-map (js/mapboxgl.Map. (clj->js map-opts))]
              (handle-events the-map opts event-keys)
              (ensure-locale! the-map)
              (fn []
                #_(js/console.log "remove map")
                (when (ifn? (:on-remove opts))
                  ((:on-remove opts)))
                (.remove the-map)))
            (catch :default _
              (set! (.-textContent container) "WebGL is disabled")
              (fn []
                (when (= "WebGL is disabled" (.-textContent container))
                  (set! (.-textContent container) ""))))))))
    deps)))

(defn get-bounds
  "Get the bounds of a given map object as a 4 float vector."
  [^js the-map]
  (into [] cat (.toArray (.getBounds the-map))))

(defn mapbox-directions
  "Create a mapbox directions object.

  Defaults are applied, see [[directions-defaults]]. Event handlers can be
  attached by passing in keys starting with `:on-`, like `:on-origin` or
  `:on-destination`. The event handlers will receive the directions object as
  their first argument."
  [{:keys [^js mapbox origin destination] :as opts}]
  (let [event-keys (event-keys opts)
        dir-opts   (into directions-defaults (apply dissoc opts event-keys))
        ^js dirs   (js/MapboxDirections. (clj->js (dissoc dir-opts :mapbox :origin :destination)))]
    (when mapbox
      (.addControl mapbox dirs))
    (when origin
      (.setOrigin dirs origin))
    (when destination
      (.setDestination dirs destination))
    (handle-events dirs opts event-keys)
    dirs))

(defn feature->coords
  "Get coordinates as a two-element JS array, given a \"feature\" object, such as
  returned by a directions object with :on-origin/:on-destination."
  [^js feature]
  (when feature
    (.. feature -feature -geometry -coordinates)))

(defn fit-bounds [^js the-map [coord1 coord2] & [opts]]
  (when (and coord1 coord2)
    (.fitBounds the-map #js [coord1 coord2] (if opts (clj->js opts) #js {}))))
